import React, { useState, useRef } from 'react';
import { toast } from '../../utils/customToast';
import { jsPDF } from 'jspdf';
import { useAuth } from '../../context/AuthContext';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { apiFetch } from '../../utils/api';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { usePlanPermissions, getCurrentPlan, UpgradeMessage } from '../../utils/planPermissions';
import Modal from '../shared/Modal';

const apiBase = process.env.REACT_APP_API_URL || '/api';

const ExportModal = ({ open, onClose, initialType = 'messages', onExportDone }) => {
    const { getAuthHeaders, user, activeTenantId } = useAuth();
    const [exportType, setExportType] = useState(initialType);
    const [format, setFormat] = useState('csv');
    const { handleApiError } = useApiErrorHandler();

    const cancelRef = useRef(null);
    const confirmRef = useRef(null);

    // Permissions basées sur le plan
    const currentPlan = getCurrentPlan(user, activeTenantId);
    const { canExport } = usePlanPermissions(currentPlan);

    if (!open) return null;

    // Fonction pour convertir le HTML en texte formaté pour le PDF
    const htmlToFormattedText = (html) => {
        if (!html) return '';

        const LANG_LABELS = {
            javascript: 'JavaScript', typescript: 'TypeScript', sql: 'SQL',
            css: 'CSS', html: 'HTML', java: 'Java', python: 'Python',
            json: 'JSON', bash: 'Bash / Shell', xml: 'XML', yaml: 'YAML',
            csharp: 'C#', cpp: 'C++', php: 'PHP'
        };

        const temp = document.createElement('div');
        temp.innerHTML = sanitizeHtml(html);

        let result = [];

        const processNode = (node, indent = 0) => {
            if (node.nodeType === Node.TEXT_NODE) {
                const text = node.textContent.trim();
                if (text) {
                    return { text, style: 'normal' };
                }
                return null;
            }

            if (node.nodeType === Node.ELEMENT_NODE) {
                const tagName = node.tagName.toLowerCase();

                switch (tagName) {
                    case 'strong':
                    case 'b':
                        return { text: node.textContent, style: 'bold' };

                    case 'em':
                    case 'i':
                        return { text: node.textContent, style: 'italic' };

                    case 'u':
                        return { text: node.textContent, style: 'underline' };

                    case 'p':
                        const pContent = [];
                        node.childNodes.forEach(child => {
                            const processed = processNode(child, indent);
                            if (processed) {
                                if (Array.isArray(processed)) {
                                    pContent.push(...processed);
                                } else {
                                    pContent.push(processed);
                                }
                            }
                        });
                        if (pContent.length > 0) {
                            return [...pContent, { text: '\n', style: 'normal' }];
                        }
                        return null;

                    case 'ul':
                    case 'ol':
                        const listItems = [];
                        node.querySelectorAll(':scope > li').forEach((li, index) => {
                            const bullet = tagName === 'ul' ? '• ' : `${index + 1}. `;
                            const indentStr = '  '.repeat(indent);

                            const liContent = [];
                            li.childNodes.forEach(child => {
                                const processed = processNode(child, indent + 1);
                                if (processed) {
                                    if (Array.isArray(processed)) {
                                        liContent.push(...processed);
                                    } else {
                                        liContent.push(processed);
                                    }
                                }
                            });

                            if (liContent.length > 0) {
                                listItems.push({ text: indentStr + bullet, style: 'normal' });
                                listItems.push(...liContent);
                                listItems.push({ text: '\n', style: 'normal' });
                            }
                        });
                        return listItems;

                    case 'pre': {
                        // Return a typed block sentinel — rendered as a dark-background code box
                        const rawLines = node.textContent.split('\n');
                        // Remove trailing empty line jsPDF artifact
                        while (rawLines.length > 0 && rawLines[rawLines.length - 1].trim() === '') rawLines.pop();
                        const langValue = node.getAttribute('data-language') || '';
                        const langLabel = langValue ? (LANG_LABELS[langValue] || langValue) : '';
                        return [{ style: 'CODEBLOCK', lines: rawLines, lang: langLabel }];
                    }

                    case 'code':
                        // Inline code (not inside <pre>) — keep as single segment
                        return { text: node.textContent, style: 'code' };

                    case 'table': {
                        // Return a typed block sentinel — rendered as actual PDF table
                        const allRows = [];
                        let headerCount = 0;
                        // thead rows
                        node.querySelectorAll('thead tr').forEach(row => {
                            const cells = Array.from(row.querySelectorAll('th, td')).map(c => c.textContent.trim());
                            allRows.push(cells);
                            headerCount++;
                        });
                        // tbody/loose tr rows
                        node.querySelectorAll('tbody tr, tr').forEach(row => {
                            if (row.closest('thead')) return;
                            const cells = Array.from(row.querySelectorAll('td, th')).map(c => c.textContent.trim());
                            if (cells.some(c => c !== '')) allRows.push(cells);
                        });
                        // If no thead, treat first row as header if it has <th>
                        if (headerCount === 0 && allRows.length > 0) {
                            const firstTr = node.querySelector('tr');
                            if (firstTr && firstTr.querySelector('th')) headerCount = 1;
                        }
                        return [{ style: 'TABLE', rows: allRows, headerCount }];
                    }

                    case 'thead':
                    case 'tbody':
                    case 'tfoot':
                    case 'tr':
                    case 'td':
                    case 'th':
                        // Handled by the 'table' case above — skip here
                        return null;

                    case 'br':
                        return { text: '\n', style: 'normal' };

                    default:
                        const children = [];
                        node.childNodes.forEach(child => {
                            const processed = processNode(child, indent);
                            if (processed) {
                                if (Array.isArray(processed)) {
                                    children.push(...processed);
                                } else {
                                    children.push(processed);
                                }
                            }
                        });
                        return children.length > 0 ? children : null;
                }
            }

            return null;
        };

        const processed = processNode(temp);
        if (Array.isArray(processed)) {
            result = processed;
        } else if (processed) {
            result = [processed];
        }

        return result;
    };

    const fetchAndExport = async () => {
        try {
            // Use export-specific endpoint which is restricted server-side
            const url = `${apiBase}/${exportType}/export`;
            const data = await apiFetch(url, { headers: getAuthHeaders() }, handleApiError);

            if (format === 'csv') {
                const delim = ';';
                const quote = (v) => '"' + String(v ?? '').replace(/"/g, '""') + '"';
                let csv = '';
                if (exportType === 'messages') {
                    const headers = ['id', 'title', 'content', 'type', 'role', 'priority', 'createdByName', 'createdAt', 'updatedAt'];
                    csv += headers.join(delim) + '\n';
                    data.forEach(m => {
                        const tempDiv = document.createElement('div');
                        tempDiv.innerHTML = sanitizeHtml(m.content || '');
                        const plainContent = tempDiv.textContent || tempDiv.innerText || '';
                        const row = [
                            m._id || m.id || '',
                            m.title || '',
                            plainContent,
                            m.type || '',
                            m.role || '',
                            m.priority || '',
                            m.createdByName || m.author || '',
                            m.createdAt || '',
                            m.updatedAt || ''
                        ].map(quote).join(delim);
                        csv += row + '\n';
                    });
                } else {
                    const headers = ['id', 'text', 'done', 'author', 'createdAt', 'updatedAt'];
                    csv += headers.join(delim) + '\n';
                    data.forEach(t => {
                        const tempDiv = document.createElement('div');
                        tempDiv.innerHTML = sanitizeHtml(t.text || '');
                        const plainText = tempDiv.textContent || tempDiv.innerText || '';
                        const row = [
                            t._id || t.id || '',
                            plainText,
                            t.done ? 'Terminé' : 'En cours',
                            t.author || '',
                            t.createdAt || '',
                            t.updatedAt || ''
                        ].map(quote).join(delim);
                        csv += row + '\n';
                    });
                }

                // Ajout du BOM UTF-8 pour une meilleure compatibilité avec Excel et caractères spéciaux
                const BOM = '\uFEFF';
                const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const urlBlob = URL.createObjectURL(blob);
                link.href = urlBlob;
                link.setAttribute('download', `${exportType}-${new Date().toISOString()}.${format}`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(urlBlob);

            } else if (format === 'pdf') {
                const doc = new jsPDF();
                const margin = 14;
                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();
                const maxWidth = pageWidth - margin * 2;

                doc.setFontSize(14);
                doc.text(`${exportType.charAt(0).toUpperCase() + exportType.slice(1)} export`, margin, 20);
                let y = 30;
                const lineHeight = 6;

                const ensurePageFor = (neededHeight) => {
                    if (y + neededHeight > pageHeight - margin) {
                        doc.addPage();
                        y = margin + 6;
                    }
                };

                const drawFormattedText = (formattedContent, x, startY, maxWidth) => {
                    let currentY = startY;
                    let currentX = x;
                    const normalFontSize = 10;
                    const codeLineH = 5;
                    const codePad = 3;

                    doc.setFontSize(normalFontSize);

                    formattedContent.forEach(segment => {
                        const { text, style } = segment;

                        // ── CODEBLOCK block (dark background + monospace) ──────────────────
                        if (style === 'CODEBLOCK') {
                            const { lines, lang } = segment;
                            const badgeH = lang ? 6 : 0;
                            const totalH = lines.length * codeLineH + codePad * 2 + 2 + badgeH;
                            // Top spacing between consecutive blocks
                            currentY += 4;
                            if (currentY + totalH > pageHeight - margin) {
                                doc.addPage();
                                currentY = margin + 6;
                            }
                            // Background
                            doc.setFillColor(30, 35, 48);
                            doc.roundedRect(x, currentY - 1, maxWidth, totalH, 2, 2, 'F');
                            // Left accent bar
                            doc.setFillColor(100, 120, 200);
                            doc.rect(x, currentY - 1, 2, totalH, 'F');
                            // Language badge (top-right)
                            if (lang) {
                                doc.setFont('helvetica', 'bold');
                                doc.setFontSize(7);
                                doc.setTextColor(150, 160, 220);
                                doc.text(lang, x + maxWidth - 2, currentY + 3.5, { align: 'right' });
                            }
                            // Code text
                            doc.setFont('courier', 'normal');
                            doc.setFontSize(8);
                            doc.setTextColor(205, 214, 244);
                            let codeY = currentY + codePad + 2 + badgeH;
                            lines.forEach(line => {
                                if (codeY > pageHeight - margin - 6) {
                                    doc.addPage();
                                    doc.setFillColor(30, 35, 48);
                                    doc.roundedRect(x, margin - 1, maxWidth, pageHeight - margin * 2, 2, 2, 'F');
                                    doc.setFillColor(100, 120, 200);
                                    doc.rect(x, margin - 1, 2, pageHeight - margin * 2, 'F');
                                    codeY = margin + 6;
                                }
                                const wrapped = doc.splitTextToSize(line === '' ? ' ' : line, maxWidth - codePad * 2 - 4);
                                wrapped.forEach(wl => {
                                    doc.text(wl, x + codePad + 4, codeY);
                                    codeY += codeLineH;
                                });
                            });
                            currentY = codeY + codePad + 4; // bottom spacing
                            currentX = x;
                            // Reset
                            doc.setTextColor(40, 40, 40);
                            doc.setFont('helvetica', 'normal');
                            doc.setFontSize(normalFontSize);
                            return;
                        }

                        // ── TABLE block (real cells with borders) ─────────────────────────
                        if (style === 'TABLE') {
                            const { rows, headerCount } = segment;
                            if (!rows || rows.length === 0) return;
                            const colCount = Math.max(...rows.map(r => r.length), 1);
                            const colWidth = maxWidth / colCount;
                            const cellH = 8;
                            const cellPad = 2.5;
                            const totalTableH = rows.length * cellH + 2;
                            if (currentY + totalTableH > pageHeight - margin) {
                                doc.addPage();
                                currentY = margin + 6;
                            }
                            doc.setFontSize(9);
                            rows.forEach((row, rowIdx) => {
                                if (currentY + cellH > pageHeight - margin) {
                                    doc.addPage();
                                    currentY = margin + 6;
                                }
                                const isHeader = rowIdx < headerCount;
                                // Row background
                                if (isHeader) {
                                    doc.setFillColor(55, 65, 100);
                                } else if (rowIdx % 2 === 0) {
                                    doc.setFillColor(245, 246, 250);
                                } else {
                                    doc.setFillColor(255, 255, 255);
                                }
                                doc.rect(x, currentY, maxWidth, cellH, 'F');
                                // Cells
                                for (let ci = 0; ci < colCount; ci++) {
                                    const cellX = x + ci * colWidth;
                                    // Border
                                    doc.setDrawColor(180, 185, 210);
                                    doc.setLineWidth(0.3);
                                    doc.rect(cellX, currentY, colWidth, cellH, 'S');
                                    // Text
                                    const cellVal = (row[ci] || '').trim();
                                    const cellText = doc.splitTextToSize(cellVal, colWidth - cellPad * 2);
                                    if (isHeader) {
                                        doc.setTextColor(255, 255, 255);
                                        doc.setFont('helvetica', 'bold');
                                    } else {
                                        doc.setTextColor(30, 30, 50);
                                        doc.setFont('helvetica', 'normal');
                                    }
                                    doc.text(cellText[0] || '', cellX + cellPad, currentY + cellH - cellPad - 0.5);
                                }
                                currentY += cellH;
                            });
                            currentY += 4;
                            currentX = x;
                            // Reset
                            doc.setTextColor(40, 40, 40);
                            doc.setFont('helvetica', 'normal');
                            doc.setFontSize(normalFontSize);
                            return;
                        }

                        // ── Inline styles ────────────────────────────────────────────────
                        switch (style) {
                            case 'bold':
                                doc.setFont(undefined, 'bold');
                                break;
                            case 'italic':
                                doc.setFont(undefined, 'italic');
                                break;
                            case 'code':
                                doc.setFont('courier', 'normal');
                                doc.setFontSize(9);
                                break;
                            default:
                                doc.setFont(undefined, 'normal');
                                break;
                        }

                        // Gérer les retours à la ligne
                        if (text === '\n') {
                            currentY += lineHeight;
                            currentX = x;
                            if (currentY > pageHeight - margin) {
                                doc.addPage();
                                currentY = margin + 6;
                            }
                        } else if (style === 'code') {
                            // Inline code snippet
                            const codeLines = doc.splitTextToSize(text || '', maxWidth - 6);
                            codeLines.forEach(cl => {
                                if (currentY > pageHeight - margin) {
                                    doc.addPage();
                                    currentY = margin + 6;
                                }
                                doc.text(cl, currentX, currentY);
                                currentX += doc.getTextWidth(cl);
                            });
                        } else {
                            // Découper le texte mot à mot
                            const words = text.split(' ');
                            words.forEach((word) => {
                                const wordWidth = doc.getTextWidth(word + ' ');
                                if (currentX + wordWidth > x + maxWidth) {
                                    currentY += lineHeight;
                                    currentX = x;
                                    if (currentY > pageHeight - margin) {
                                        doc.addPage();
                                        currentY = margin + 6;
                                    }
                                }
                                if (style === 'underline') {
                                    const tw = doc.getTextWidth(word);
                                    doc.text(word, currentX, currentY);
                                    doc.line(currentX, currentY + 0.5, currentX + tw, currentY + 0.5);
                                } else {
                                    doc.text(word, currentX, currentY);
                                }
                                currentX += wordWidth;
                            });
                        }

                        // Réinitialiser la police
                        doc.setFont(undefined, 'normal');
                        doc.setFontSize(normalFontSize);
                    });

                    return currentY;
                };

                const drawEntry = (id, title, author, typeField, priority, body, createdAt, updatedAt) => {
                    // Convertir le contenu HTML en texte formaté
                    const formattedContent = htmlToFormattedText(body);

                    // Calculer la hauteur nécessaire (estimation)
                    const estimatedHeight = Math.max(formattedContent.length * lineHeight + 60, 100);
                    ensurePageFor(estimatedHeight);

                    // En-tête
                    const titleLines = doc.splitTextToSize(title || '', maxWidth);
                    const metaLines = doc.splitTextToSize(`Auteur: ${author || ''}   Type: ${typeField || ''}`, maxWidth);
                    const priorityLine = doc.splitTextToSize(`Priorité: ${priority || ''}`, maxWidth);
                    const createdLines = doc.splitTextToSize(`Créé: ${createdAt || ''}`, maxWidth);
                    const updatedLines = updatedAt ? doc.splitTextToSize(`Modifié: ${updatedAt}`, maxWidth) : [];

                    doc.setFontSize(12);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(10, 10, 10);
                    doc.text(titleLines, margin, y);
                    y += titleLines.length * lineHeight;

                    doc.setFontSize(10);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(60, 60, 60);
                    doc.text(metaLines, margin, y);
                    y += metaLines.length * lineHeight + 2;

                    doc.setFontSize(10);
                    doc.setTextColor(80, 80, 80);
                    doc.text(priorityLine, margin, y);
                    y += priorityLine.length * lineHeight;

                    doc.setFontSize(9);
                    doc.setTextColor(100, 100, 100);
                    doc.text(createdLines, margin, y);
                    y += createdLines.length * lineHeight;
                    if (updatedLines.length) {
                        doc.text(updatedLines, margin, y);
                        y += updatedLines.length * lineHeight;
                    }

                    // Contenu formaté
                    if (formattedContent.length > 0) {
                        doc.setFontSize(10);
                        doc.setTextColor(30, 30, 30);
                        doc.setFont('helvetica', 'bold');
                        doc.text('Contenu :', margin, y);
                        y += lineHeight;

                        doc.setTextColor(40, 40, 40);
                        y = drawFormattedText(formattedContent, margin, y, maxWidth);
                        y += lineHeight;
                    }

                    y += 6;
                    doc.setDrawColor(200);
                    doc.setLineWidth(0.4);
                    doc.line(margin, y, pageWidth - margin, y);
                    y += 10;
                };

                if (exportType === 'messages') {
                    for (const m of data) {
                        const id = m._id || m.id || '';
                        const title = m.title || '';
                        const author = m.createdByName || m.author || '';
                        const typeField = m.type || '';
                        const priority = m.priority || '';
                        const body = (m.content || '').trim();
                        const createdAt = m.createdAt ? new Date(m.createdAt).toLocaleString() : '';
                        const updatedAt = m.updatedAt ? new Date(m.updatedAt).toLocaleString() : '';
                        drawEntry(id, title, author, typeField, priority, body, createdAt, updatedAt);
                    }
                } else {
                    // Pour les todos : pas de titre, juste le contenu
                    for (const t of data) {
                        const id = t._id || t.id || '';
                        const author = t.author || '';
                        const typeField = t.done ? 'Terminé' : 'En cours';
                        const createdAt = t.createdAt ? new Date(t.createdAt).toLocaleString() : '';
                        const updatedAt = t.updatedAt ? new Date(t.updatedAt).toLocaleString() : '';

                        // Le texte du todo contient du HTML - le traiter comme body
                        const body = (t.text || '').trim();

                        // Pas de titre pour les todos, utiliser une chaîne vide
                        drawEntry(id, '', author, typeField, body, createdAt, updatedAt);
                    }
                }

                try {
                    const pageCount = doc.internal.getNumberOfPages();
                    doc.setFontSize(9);
                    for (let i = 1; i <= pageCount; i++) {
                        doc.setPage(i);
                        doc.text(`Page ${i} / ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
                    }
                } catch (e) {
                    console.warn('Page numbering failed', e);
                }

                doc.save(`${exportType}-${new Date().toISOString()}.pdf`);
            } else if (format === 'json') {
                // Export all fields for messages/todos as pretty-printed JSON
                let exportData = [];
                if (exportType === 'messages') {
                    exportData = data.map(m => ({
                        id: m._id || m.id || '',
                        title: m.title || '',
                        content: m.content || '',
                        type: m.type || '',
                        role: m.role || '',
                        priority: m.priority || '',
                        createdByName: m.createdByName || m.author || '',
                        createdAt: m.createdAt || '',
                        updatedAt: m.updatedAt || ''
                    }));
                } else {
                    exportData = data.map(t => ({
                        id: t._id || t.id || '',
                        text: t.text || '',
                        done: t.done ? 'Terminé' : 'En cours',
                        author: t.author || '',
                        createdAt: t.createdAt || '',
                        updatedAt: t.updatedAt || ''
                    }));
                }
                const jsonString = JSON.stringify(exportData, null, 2);
                const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8;' });
                const link = document.createElement('a');
                const urlBlob = URL.createObjectURL(blob);
                link.href = urlBlob;
                link.setAttribute('download', `${exportType}-${new Date().toISOString()}.json`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(urlBlob);
            }

            if (typeof onExportDone === 'function') onExportDone();
            toast.success('Export terminé', { position: 'top-right' });
        } catch (err) {
            console.error('Export error', err);
            toast.error('Erreur lors de l\'export.', { position: 'top-right' });
            throw err;
        }
    };

    // Modal actions
    const modalActions = (
        <>
            <button
                ref={cancelRef}
                onClick={onClose}
                className="flex-1 px-4 py-2 rounded border dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            >
                Fermer
            </button>
            <button
                ref={confirmRef}
                onClick={fetchAndExport}
                disabled={!canExport}
                className={`flex-1 py-2 px-4 rounded transition ${canExport
                        ? 'bg-primary text-white dark:text-black hover:bg-primary-hover'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
            >
                Exporter
            </button>
        </>
    );

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Exporter"
            actions={modalActions}
            initialFocusRef={canExport ? confirmRef : cancelRef}
        >
            <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Type</label>
                    <select
                        value={exportType}
                        onChange={e => setExportType(e.target.value)}
                        className="w-full p-2 rounded bg-gray-100 dark:bg-gray-700 dark:text-white border dark:border-gray-600"
                    >
                        <option value="messages">Messages</option>
                        <option value="todos">Todos</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Format</label>
                    <select
                        value={format}
                        onChange={e => setFormat(e.target.value)}
                        className="w-full p-2 rounded bg-gray-100 dark:bg-gray-700 dark:text-white border dark:border-gray-600"
                    >
                        <option value="csv">CSV</option>
                        <option value="pdf">PDF</option>
                        <option value="json">JSON</option>
                    </select>
                </div>
            </div>

            {!canExport && (
                <UpgradeMessage
                    feature="exports"
                    currentPlan={currentPlan}
                    className="mb-4"
                />
            )}

            {canExport && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                        💡 L'export PDF conserve le formatage (gras, italique, souligné, listes, code)
                    </p>
                </div>
            )}
        </Modal>
    );
};

export default ExportModal;